// analysis rows: ia_analysis is the AI output, custom_analysis the admin edit.
// Standard analyses store categories ({ "Title": ["markdown", …] }) directly; custom
// analyses (type "custom-…") wrap them as { prompt, analyse }.

import { analysisFailureMessage } from '../../utils/analysisFailure.js';

export const isCustomAnalysis = (analysis) => !!analysis?.type?.startsWith('custom');

export const parseAnalysisData = (data) => {
  if (!data) return {};
  if (typeof data !== 'string') return data;
  try {
    return JSON.parse(data);
  } catch {
    return {};
  }
};

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/** Categories to display: the admin edit when there is one, the AI output otherwise. */
export const analysisContent = (analysis) => {
  const custom = parseAnalysisData(analysis?.custom_analysis);
  const customType = isCustomAnalysis(analysis);
  if (isObject(custom) && Object.keys(custom).length > 0) {
    if (isObject(custom.analyse)) return custom.analyse;
    if (!customType) return custom;
  }
  const ia = parseAnalysisData(analysis?.ia_analysis);
  if (!isObject(ia)) return {};
  if (!customType) return ia;
  if (isObject(ia.analyse)) return ia.analyse;
  const { prompt, model, usage, ...categories } = ia;
  return categories;
};

/** A category holds a list of markdown strings; older rows may hold a single string. */
export const toEntries = (value) => {
  const list = Array.isArray(value) ? value : [value];
  return list
    .filter((entry) => entry !== undefined && entry !== null)
    .map((entry) => (typeof entry === 'string' ? entry : JSON.stringify(entry)));
};

/** Prompt of a custom analysis (edited first, then the original one). */
export const analysisPrompt = (analysis) => {
  if (!analysis) return '';
  const custom = parseAnalysisData(analysis.custom_analysis);
  if (custom?.prompt) return custom.prompt;
  const ia = parseAnalysisData(analysis.ia_analysis);
  return ia?.prompt || analysis.prompt || '';
};

/** Analyses in the order of the client's configuration; unknown types last. */
export const sortAnalysesByConfig = (analyses, weeklyConfig) => {
  const order = Array.isArray(weeklyConfig?.analysis)
    ? new Map(weeklyConfig.analysis.map((item, index) => [item.slug, index]))
    : null;
  if (!order) return analyses;
  return [...analyses].sort((a, b) => (order.get(a.type) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.type) ?? Number.MAX_SAFE_INTEGER));
};

export const analysisTitle = (analysis, weeklyConfig) => {
  const configured = weeklyConfig?.analysis?.find((c) => c.slug === analysis?.type);
  return analysis?.title || configured?.name || analysis?.type || 'Analyse';
};

/** Error message stored by the analyzer on a failed analysis ({ error: [message] }). */
export const analysisError = (analysis) => {
  if (analysis?.status !== 'failed') return null;
  const error = parseAnalysisData(analysis.ia_analysis)?.error;
  // Rows written before the masking still hold the raw provider error
  return analysisFailureMessage(Array.isArray(error) ? error.join(' ') : error || '');
};
